
import React, { createContext, useContext, ReactNode, useEffect, useRef, useState, useCallback } from 'react';
import { Conversation, ChatMessage } from '../../views/crm-pages/conversation/types';
export interface RealtimeEnvelope {
  event_type: string;
  topic: "tenant" | "conversation";
  tenant_id: string;
  conversation_id?: string | null;
  message_id?: string | null;
  file_id?: string | null;
  correlation_id?: string | null;
  payload: Record<string, unknown>;
  timestamp: string;
}

export type { Conversation, ChatMessage as Message };

interface WebSocketContextValue {
  isConnected: boolean;
  isReconnecting: boolean;
  subscribe: (conversationId: string) => void;
  unsubscribe: (conversationId: string) => void;
  sendPing: () => void;
  connect: () => void;
  disconnect: () => void;
  currentConversationId: string | null;
  onMessageCreated?: (message: ChatMessage, conversationId: string) => void;
  onMessageStatusUpdated?: (message: ChatMessage, conversationId: string) => void;
  onConversationCreated?: (conversation: Conversation) => void;
  onConversationUpdated?: (conversation: Conversation) => void;
}

interface WebSocketProviderProps {
  children: ReactNode;
  onConnected?: () => void;
  onDisconnected?: () => void;
  onMessageCreated?: (message: ChatMessage, conversationId: string) => void;
  onMessageStatusUpdated?: (message: ChatMessage, conversationId: string) => void;
  onConversationCreated?: (conversation: Conversation) => void;
  onConversationUpdated?: (conversation: Conversation) => void;
}

const WebSocketContext = createContext<WebSocketContextValue | undefined>(undefined);

const WS_BASE_URL =
  "wss://api.crm.dalmlabs.com";
  //'wss://convohub-backend-0iiy.onrender.com';

// Stop auto-retrying after this many consecutive failures — counts retries
// only, not the initial connection attempt, so a value of 1 means "try
// once, retry once more if that fails, then give up" (2 attempts total).
// A real activity signal (tab refocus, etc.) still gets a fresh try beyond
// this, see ensureConnected below.
const MAX_RECONNECT_ATTEMPTS = 1;

const getDisplayName = (value: unknown): string => {
  const item = value as Record<string, any> | undefined;
  if (!item) return "";

  const fullName = [item.first_name || item.firstName, item.last_name || item.lastName]
    .filter(Boolean)
    .join(" ");

  return (
    item.name ||
    item.full_name ||
    item.fullName ||
    item.title ||
    fullName ||
    item.phone ||
    item.recipient ||
    item.external_thread_id ||
    ""
  );
};

const getMessageSenderName = (event: RealtimeEnvelope, message: ChatMessage): string => {
  const payload = event.payload as Record<string, any>;
  return (
    getDisplayName(payload?.contact) ||
    getDisplayName(payload?.customer) ||
    getDisplayName(payload?.lead) ||
    getDisplayName(payload?.conversation) ||
    payload?.sender_name ||
    payload?.from_name ||
    (message as any)?.sender_name ||
    (message as any)?.from ||
    message.recipient ||
    "New customer"
  );
};

export function WebSocketProvider({
  children,
  onConnected,
  onDisconnected,
  onMessageCreated: propOnMessageCreated,
  onMessageStatusUpdated: propOnMessageStatusUpdated,
  onConversationCreated: propOnConversationCreated,
  onConversationUpdated: propOnConversationUpdated,
}: WebSocketProviderProps) {
  const [isConnected, setIsConnected] = useState(false);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectAttemptsRef = useRef<number>(0);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const currentConversationIdRef = useRef<string | null>(null);
  const shouldReconnectRef = useRef<boolean>(true);

  const getAccessToken = useCallback(() => {
    const token = localStorage.getItem("authToken");

    return token;
  }, []);

  // components/crm/socket-context.tsx - Update the handleRealtimeEvent function

  const handleRealtimeEvent = useCallback((event: RealtimeEnvelope) => {
    switch (event.event_type) {
      case "connection.ready":
        break;

      // components/crm/socket-context.tsx - Update the message.created case

      case "message.created": {
        const message = event.payload?.message as ChatMessage | undefined;
        const conversation = event.payload?.conversation as any;

        if (message && event.conversation_id) {

          // 1. Always dispatch ws-message-created so ChatArea updates
          window.dispatchEvent(new CustomEvent('ws-message-created', {
            detail: {
              message,
              conversationId: event.conversation_id,
              conversation,
            },
          }));

          // 2. Only handle inbound messages for notifications + unread counts
          if (message.direction === "inbound") {
            const senderName =
              getDisplayName(conversation) ||
              getMessageSenderName(event, message);

            const preview =
              message.body?.trim() ||
              (message.type === "image" && "📷 Image") ||
              (message.type === "video" && "🎥 Video") ||
              (message.type === "audio" && "🎤 Audio") ||
              (message.type === "document" && "📄 Document") ||
              "New message";

            // 3. Store notification in localStorage
            const notifications = JSON.parse(localStorage.getItem('ws_notifications') || '[]');
            notifications.unshift({
              id: message.id,
              conversationId: event.conversation_id,
              senderName,
              preview,
              timestamp: new Date().toISOString(),
              read: false,
              direction: message.direction,
            });
            localStorage.setItem('ws_notifications', JSON.stringify(notifications.slice(0, 50)));

            // 4. Dispatch ws-notification → GlobalNotificationHandler shows toast
            window.dispatchEvent(new CustomEvent('ws-notification', {
              detail: {
                message,
                conversationId: event.conversation_id,
                senderName,
                preview,
              },
            }));

            // 5. Update unread counts in localStorage
            const unreadCounts = JSON.parse(localStorage.getItem('ws_unread_counts') || '{}');
            unreadCounts[event.conversation_id] = (unreadCounts[event.conversation_id] || 0) + 1;
            localStorage.setItem('ws_unread_counts', JSON.stringify(unreadCounts));

            // 6. Dispatch ws-unread-updated → ConversationDashboard updates badges
            window.dispatchEvent(new CustomEvent('ws-unread-updated', {
              detail: {
                conversationId: event.conversation_id,
                counts: unreadCounts,
              },
            }));
          }

          propOnMessageCreated?.(message, event.conversation_id);
        }
        break;
      }
      case "conversation.updated": {
        const conversation = event.payload?.conversation as any;
        if (conversation) {
          propOnConversationUpdated?.(conversation);

          window.dispatchEvent(new CustomEvent('ws-conversation-updated', {
            detail: { conversation },
          }));

          const isNewMessage = event.correlation_id?.startsWith('message:');
          // The currently-open conversation is subscribed on its own
          // conversation-scoped channel too, so it already gets this exact
          // new-message notification from the "message.created" case above —
          // this tenant-wide broadcast would otherwise duplicate it. Any
          // other (not-open) conversation only ever gets this one, so it
          // still needs to fire normally.
          const isCurrentlyOpenConversation = currentConversationIdRef.current === conversation.id;

          if (isNewMessage && !isCurrentlyOpenConversation) {
            const senderName =
              conversation.title ||
              conversation.external_thread_id ||
              'New message';

            const preview =
              conversation.last_message ||
              conversation.last_message_body ||
              conversation.message ||
              'New message';

            window.dispatchEvent(new CustomEvent('ws-notification', {
              detail: {
                message: { direction: 'inbound' },
                conversationId: conversation.id,
                senderName,
                preview,
              },
            }));

            const unreadCounts = JSON.parse(localStorage.getItem('ws_unread_counts') || '{}');
            unreadCounts[conversation.id] = (unreadCounts[conversation.id] || 0) + 1;
            localStorage.setItem('ws_unread_counts', JSON.stringify(unreadCounts));

            window.dispatchEvent(new CustomEvent('ws-unread-updated', {
              detail: {
                conversationId: conversation.id,
                counts: unreadCounts,
              },
            }));
          }
        }
        break;
      }

    }
  }, [propOnMessageCreated, propOnMessageStatusUpdated, propOnConversationCreated, propOnConversationUpdated]);
 
  const attemptReconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }

    // Give up after a bounded number of failures instead of retrying every
    // 10s forever — a dead/misconfigured endpoint shouldn't keep the socket
    // hammering it indefinitely. Focus/storage/visibility events (and the
    // periodic ensureConnected check) still call connect() directly, so a
    // real recovery (backend comes back, tab regains focus) still reconnects.
    if (reconnectAttemptsRef.current >= MAX_RECONNECT_ATTEMPTS) {
      setIsReconnecting(false);
      return;
    }

    const delays = [1000, 2000, 5000, 10000];
    const delay = delays[Math.min(reconnectAttemptsRef.current, delays.length - 1)];

    setIsReconnecting(true);

    reconnectTimeoutRef.current = setTimeout(() => {
      reconnectAttemptsRef.current++;
      connect();
    }, delay);
  }, []);

  const connect = useCallback(() => {
    shouldReconnectRef.current = true;
    const token = getAccessToken();
    if (!token) {
      console.warn("⚠️ No access token available for WebSocket connection");
      return;
    }

    if (wsRef.current?.readyState === WebSocket.OPEN) {
      return;
    }

    if (wsRef.current?.readyState === WebSocket.CONNECTING) {
      return;
    }

    const wsUrl = `${WS_BASE_URL}/api/v1/ws?token=${encodeURIComponent(token)}`;


    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;
    
      ws.onopen = () => {
        setIsConnected(true);
        setIsReconnecting(false);
        reconnectAttemptsRef.current = 0;

        // global updates
        ws.send(
          JSON.stringify({
            action: "subscribe",
            topic: "tenant",
          })
        );

        // re-subscribe active conversation after reconnect
        if (currentConversationIdRef.current) {
          ws.send(
            JSON.stringify({
              action: "subscribe",
              conversation_id: currentConversationIdRef.current,
            })
          );
        }

        onConnected?.();
      };

      ws.onmessage = (event) => {
        try {
          const data: RealtimeEnvelope = JSON.parse(event.data);
          handleRealtimeEvent(data);
        } catch (error) {

        }
      };

      ws.onclose = (event) => {
        setIsConnected(false);
        onDisconnected?.();

        if (shouldReconnectRef.current) {
          attemptReconnect();
        }
      };
    } catch (error) {
    }
  }, [getAccessToken, onConnected, onDisconnected, handleRealtimeEvent, attemptReconnect]);

  const disconnect = useCallback(() => {
    shouldReconnectRef.current = false;
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = undefined;
    }
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    setIsConnected(false);
    setIsReconnecting(false);
  }, []);

  const subscribe = useCallback(
    (conversationId: string) => {
      currentConversationIdRef.current =
        conversationId;
      if (
        wsRef.current?.readyState ===
        WebSocket.OPEN
      ) {
        wsRef.current.send(
          JSON.stringify({
            action: "subscribe",
            conversation_id: conversationId,
          })
        );
      }
    },
    []
  );



  const unsubscribe = useCallback((conversationId: string) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      const unsubscribeMsg = {
        action: "unsubscribe",
        conversation_id: conversationId,
      };
      wsRef.current.send(JSON.stringify(unsubscribeMsg));
      if (currentConversationIdRef.current === conversationId) {
        currentConversationIdRef.current = null;
      }
    }
  }, []);

  const sendPing = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ action: "ping" }));
    }
  }, []);

  // In socket-context.tsx, update the ensureConnected function:

  const ensureConnected = useCallback((isUserTriggered: boolean = false) => {
    const token = getAccessToken();
    const readyState = wsRef.current?.readyState;

    if (!token) {
      if (wsRef.current) disconnect();
      return;
    }

    if (readyState === WebSocket.OPEN || readyState === WebSocket.CONNECTING) {
      return;
    }

    if (isUserTriggered) {
      // A real activity signal (tab refocused, storage changed in another
      // tab, visibility restored) is worth a fresh attempt even if the
      // ambient retry budget below ran out — otherwise "the user came back"
      // would be silently ignored forever after one bad stretch.
      reconnectAttemptsRef.current = 0;
      connect();
      return;
    }

    // The periodic poll, unlike a real activity signal, has to respect the
    // same give-up budget attemptReconnect uses — otherwise capping that
    // backoff schedule doesn't actually stop anything, since this interval
    // was calling connect() directly regardless of it.
    if (reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS) {
      connect();
    }
  }, [connect, disconnect, getAccessToken]);

  const ensureConnectedOnActivity = useCallback(() => ensureConnected(true), [ensureConnected]);

  // Tracks whether a real auth token is present at all — everything below
  // (connecting, the 5s reconnect-budget check, the focus/storage/visibility
  // listeners) is gated on this, so while logged out none of it runs: no WS
  // attempts, no polling, nothing "called". This is mounted once at the App
  // root (above the router, above AuthGuard), so it has to notice auth
  // state on its own rather than being unmounted/remounted on login/logout —
  // checked via a cheap in-memory localStorage read (not a network call),
  // plus the "storage" event for cross-tab login/logout.
  const [hasToken, setHasToken] = useState<boolean>(!!getAccessToken());

  useEffect(() => {
    const checkToken = () => {
      const present = !!getAccessToken();
      setHasToken((prev) => (prev === present ? prev : present));
    };
    checkToken();
    const authCheckInterval = window.setInterval(checkToken, 2000);
    window.addEventListener("storage", checkToken);
    return () => {
      window.clearInterval(authCheckInterval);
      window.removeEventListener("storage", checkToken);
    };
  }, [getAccessToken]);

  useEffect(() => {
    if (!hasToken) {
      // Logged out (or not yet logged in) — make sure nothing is left
      // connected or retrying, and don't set up any of the reconnect
      // machinery below at all.
      disconnect();
      return;
    }

    // Fresh budget every time auth (re)appears, so a just-completed login
    // isn't penalized by a give-up state left over from before.
    reconnectAttemptsRef.current = 0;
    ensureConnected(true);

    const interval = window.setInterval(() => ensureConnected(false), 5000); // Check every 5 seconds

    window.addEventListener("focus", ensureConnectedOnActivity);
    window.addEventListener("storage", ensureConnectedOnActivity);
    document.addEventListener("visibilitychange", ensureConnectedOnActivity);

    return () => {
      window.clearInterval(interval);
      window.removeEventListener("focus", ensureConnectedOnActivity);
      window.removeEventListener("storage", ensureConnectedOnActivity);
      document.removeEventListener("visibilitychange", ensureConnectedOnActivity);
      disconnect();
    };
  }, [hasToken, disconnect, ensureConnected, ensureConnectedOnActivity]);

  return (
    <WebSocketContext.Provider
      value={{
        isConnected,
        isReconnecting,
        subscribe,
        unsubscribe,
        sendPing,
        connect,
        disconnect,
        currentConversationId: currentConversationIdRef.current,
        onMessageCreated: propOnMessageCreated,
        onMessageStatusUpdated: propOnMessageStatusUpdated,
        onConversationCreated: propOnConversationCreated,
        onConversationUpdated: propOnConversationUpdated,
      }}
    >
      {children}
    </WebSocketContext.Provider>
  );
}

export function useWebSocketContext() {
  const context = useContext(WebSocketContext);
  if (context === undefined) {
    throw new Error('useWebSocketContext must be used within a WebSocketProvider');
  }
  return context;
}
